/**
 * ExplainPanel – right-side drawer that explains the current screen.
 *
 * Sections:
 *   1. Title
 *   2. Description
 *   3. Highlights (bullets, with optional "Show me where")
 *   4. Next step
 *   5. Optional voice + smart-AI toggle
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Sparkles, Volume2, VolumeX, MapPin, ArrowRight } from 'lucide-react';
import { AppButtonLoader } from '@/modules/shared/components/ui/AppLoading';
import { Button } from '@/modules/shared/components/ui/button';
import { Switch } from '@/modules/shared/components/ui/switch';
import { useExplain } from '@/modules/shared/contexts/ExplainContext';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import { extractUIContext, type UIContext } from '@/modules/shared/services/UIContextExtractor';
import { generateExplanation, type Explanation } from '@/modules/shared/services/ExplainEngine';
import { highlightSelector } from '@/modules/shared/services/uiHighlight';
import { demoNarration } from '@/modules/shared/services/DemoNarrationService';

export const EXPLAIN_PANEL_WIDTH = 380;

export function ExplainPanel() {
  const { isOpen, closeExplainPanel, smartAiMode, setSmartAiMode } = useExplain();
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.programRoleName ?? user?.role;

  const [refreshTick, setRefreshTick] = useState(0);
  const [speaking, setSpeaking] = useState(false);

  // Re-extract context every time the panel is opened OR the route changes
  // while it's open, so users always see the latest screen state.
  useEffect(() => {
    if (isOpen) setRefreshTick((t) => t + 1);
  }, [isOpen, location.pathname]);

  const context: UIContext = useMemo(
    () => extractUIContext({ pathname: location.pathname, role }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, role, isOpen, refreshTick],
  );

  const explanation: Explanation = useMemo(() => generateExplanation(context), [context]);

  // Stop narration when panel closes.
  useEffect(() => {
    if (!isOpen) {
      demoNarration.stop();
      setSpeaking(false);
    }
  }, [isOpen]);

  const handleSpeak = () => {
    if (speaking) {
      demoNarration.stop();
      setSpeaking(false);
      return;
    }
    setSpeaking(true);
    const text = `${explanation.title}. ${explanation.description}`;
    demoNarration.speak(text, () => setSpeaking(false), 'STORYTELLING');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop (mobile/tap-to-close). Desktop keeps the app interactive. */}
      <div
        className="fixed inset-0 z-[9989] bg-black/20 lg:bg-transparent lg:pointer-events-none"
        onClick={closeExplainPanel}
        aria-hidden
      />

      <aside
        className="fixed top-0 right-0 h-screen bg-surface-card border-l border-border shadow-xl z-[9990] flex flex-col"
        style={{ width: EXPLAIN_PANEL_WIDTH }}
        role="complementary"
        aria-label="Explain this screen"
      >
        {/* ── Header ───────────────────────────────────────────── */}
        <header className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <h2 className="text-[14px] font-semibold text-foreground truncate">
              Explain this screen
            </h2>
          </div>
          <button
            type="button"
            onClick={closeExplainPanel}
            className="p-1 rounded hover:bg-surface-hover text-muted-foreground"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </header>

        {/* ── Body (scrollable) ────────────────────────────────── */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
          <section>
            <h3 className="text-[14px] font-semibold text-foreground">
              {explanation.title}
            </h3>
            <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
              {explanation.description}
            </p>
          </section>

          {explanation.highlights.length > 0 && (
            <section>
              <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-2">
                Key highlights
              </h4>
              <ul className="space-y-2">
                {explanation.highlights.map((h, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-[12px] text-foreground"
                  >
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <span className="flex-1 leading-relaxed">{h.text}</span>
                    {h.target && (
                      <button
                        type="button"
                        onClick={() => highlightSelector(h.target)}
                        className="text-primary hover:bg-primary/10 rounded p-1 flex-shrink-0"
                        title="Show me where"
                        aria-label="Show me where"
                      >
                        <MapPin className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="rounded-md border border-border bg-surface-muted px-3 py-2.5">
            <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1">
              What to do next
            </h4>
            <p className="text-[12px] text-foreground flex items-start gap-2">
              <ArrowRight className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />
              <span>{explanation.nextStep}</span>
            </p>
          </section>

          {/* ── Smart AI toggle (future-ready, no-op for now) ─── */}
          <section className="flex items-center justify-between rounded-md border border-dashed border-border px-3 py-2">
            <div>
              <p className="text-[12px] font-medium text-foreground">Smart AI mode</p>
              <p className="text-[11px] text-muted-foreground">
                Enhanced explanations from the backend AI service.
              </p>
            </div>
            <Switch checked={smartAiMode} onCheckedChange={setSmartAiMode} />
          </section>
        </div>

        {/* ── Footer actions ───────────────────────────────────── */}
        <footer className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border bg-surface-muted">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSpeak}
            className="text-[12px] gap-1.5"
          >
            {speaking ? (
              <>
                <VolumeX className="w-3.5 h-3.5" /> Stop
              </>
            ) : (
              <>
                <Volume2 className="w-3.5 h-3.5" /> Listen
              </>
            )}
          </Button>
          {smartAiMode ? (
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
              <AppButtonLoader compact /> AI mode (coming soon)
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground">Rule-based</span>
          )}
        </footer>
      </aside>
    </>
  );
}
